import React, { useState, useEffect, useCallback } from "react"
import { useMutation } from "@apollo/react-hooks"
import { withRouter } from "react-router"
import withModal from "components/modal"
import { CustomServerSideReactTableNew } from "components/utility"
import { Button, Card, CardHeader, CardBody, CardFooter } from "reactstrap"
import ReactTable from "react-table"
import { withBreadcrumb, useBread } from "components/breadcrumb"
import { BarLoader } from "components/spinner"
import { userInfo, userRole } from "utils"
import {
  GET_PRICEBOOKS,
  CREATE_ORDER_WITH_DETAIL,
  SUM_PRICEBOOKS,
  GET_TABLE_FIELDS
} from "../gql"

document.body.scrollTop = 0 // For Safari
document.documentElement.scrollTop = 0 // For Chrome, Firefox, IE and Opera

const ContractProducts = (props) => {
  useBread(
    { to: props.match.url, name: "合約品項 (非合約品項無法合併結單)" },
    true
  )

  const [selectedItems, setSelectedItems] = useState([])
  const [productQuantity, setProductQuantity] = useState({})
  const [subTotal, setSubtotal] = useState(0)
  const [delivery, setDelivery] = useState("WITH_RECEIPT")
  const [createOrderWithDetail] = useMutation(CREATE_ORDER_WITH_DETAIL)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState()

  const columnDefs = [
    { title: "產品編號", name: "productNumber", type: "input" },
    { title: "產品規格", name: "description", type: "input" }
  ]

  const fieldsDefinitions = () => {
    return [
      {
        id: "productId",
        style: columStyle,
        accessor: (data) => data?.product?.id,
        show: false
      },
      {
        id: "productNumber",
        Header: "產品編號",
        style: columStyle,
        accessor: (data) => data?.product?.productNumber
      },
      {
        id: "description",
        Header: "產品規格",
        style: columStyle,
        accessor: (data) => data?.product?.description
      },
      {
        id: "price",
        Header: "合約價格",
        width: 120,
        style: columStyle,
        accessor: (data) => `$${data?.price}`
      },
      {
        Header: "數量",
        width: 150,
        style: columStyle,
        Cell: (cell) => {
          const quantity = productQuantity?.[cell.row.id] || 1
          const itemId = cell.row.id

          return (
            <div className="input-group">
              <div className="input-group-prepend">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => {
                    setProductQuantity({
                      ...productQuantity,
                      [itemId]: quantity - 1
                    })
                  }}
                  disabled={
                    productQuantity[itemId] <= 1 || !productQuantity[itemId]
                  }
                >
                  <i className="fas fa-minus" />
                </button>
              </div>
              <input
                type="text"
                pattern="[0-9]*"
                className="form-control"
                style={{ textAlign: "center" }}
                defaultValue={quantity}
                onChange={(e) =>
                  setProductQuantity({
                    ...productQuantity,
                    [itemId]: e.target.value
                  })
                }
              />
              <div className="input-group-prepend">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => {
                    setProductQuantity({
                      ...productQuantity,
                      [itemId]: quantity + 1
                    })
                  }}
                >
                  <i className="fas fa-plus" />
                </button>
              </div>
            </div>
          )
        }
      },
      {
        width: 60,
        style: columStyle,
        Cell: (cell) => {
          return (
            <Button
              style={{ width: "50px" }}
              color={"warning"}
              onClick={() => addItem(cell.row)}
            >
              <div>加入</div>
            </Button>
          )
        }
      }
    ]
  }

  const addItem = (row) => {
    const dateTime = Date.now()
    const timestamp = Math.floor(dateTime / 10)
    const quantity = productQuantity[row.id] || 1
    const item = {
      id: row.id,
      productNumber: row.productNumber,
      description: row.description,
      quantity,
      _id: timestamp,
      productId: row.productId,
      price: row.price
    }
    setSelectedItems([...selectedItems, item])
  }

  const removeItem = (row) => {
    setSelectedItems(selectedItems.filter((item) => item._id !== row._id))
  }

  useEffect(() => {
    getSelectedTotal()
  }, [getSelectedTotal, selectedItems])

  const getSelectedTotal = useCallback(() => {
    let total = 0
    selectedItems.forEach((item) => {
      total +=
        parseInt(item.price.replace("$", ""), 10) * parseInt(item.quantity, 10)
    })
    setSubtotal(total)
  }, [selectedItems])

  const showAlertModal = (callBack) => {
    props.modal.setCentered(false)
    props.modal.setModalSize("md")
    props.modal.setHeaderText("系統提示")
    props.modal.setModalContent("請再次確認品項、數量及金額是否正確")
    props.modal.setOnSubmit(() => {
      callBack()
    })
    props.modal.toggle()
  }

  return (
    <div>
      <CustomServerSideReactTableNew
        take={5}
        columnDefs={columnDefs}
        queryObjectGql={GET_PRICEBOOKS}
        sumTotalQueryObjectGql={SUM_PRICEBOOKS}
        fieldsDefGql={GET_TABLE_FIELDS}
        objectRoute={props.match.url}
        fieldsDef={fieldsDefinitions()}
      />

      <Card>
        <CardHeader>已選擇項目</CardHeader>
        <CardBody>
          <ReactTable
            defaultPageSize={5}
            getTdProps={() => ({
              style: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center"
              }
            })}
            data={selectedItems}
            columns={[
              {
                Header: "id",
                accessor: "id",
                show: false
              },
              {
                id: "productId",
                style: columStyle,
                accessor: "productId",
                show: false
              },
              {
                Header: "_id",
                accessor: "_id",
                show: false
              },
              {
                id: "productNumber",
                Header: "產品編號",
                style: columStyle,
                accessor: "productNumber"
              },
              {
                id: "description",
                Header: "產品規格",
                style: columStyle,
                accessor: "description"
              },
              {
                id: "price",
                Header: "合約價格",
                width: 120,
                style: columStyle,
                accessor: (data) => `${data?.price}`
              },
              {
                id: "quantity",
                Header: "數量",
                width: 150,
                accessor: "quantity",
                style: columStyle,
                Cell: (cell) => {
                  const { quantity } = cell.row
                  const itemId = cell.row._id
                  return (
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => {
                            setSelectedItems(
                              selectedItems.map((item) => {
                                if (item._id === itemId) {
                                  item.quantity -= 1
                                }
                                return item
                              })
                            )
                          }}
                          disabled={quantity <= 1}
                        >
                          <i className="fas fa-minus" />
                        </button>
                      </div>
                      <input
                        type="text"
                        pattern="[0-9]*"
                        className="form-control"
                        style={{ textAlign: "center" }}
                        value={quantity}
                        onChange={(e) => {
                          if (!e.target.validity.valid) {
                            return
                          }

                          setSelectedItems(
                            selectedItems.map((item) => {
                              if (item._id === itemId) {
                                item.quantity = e.target.value
                              }
                              return item
                            })
                          )
                        }}
                      />
                      <div className="input-group-prepend">
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => {
                            setSelectedItems(
                              selectedItems.map((item) => {
                                if (item._id === itemId) {
                                  item.quantity += 1
                                }
                                return item
                              })
                            )
                          }}
                        >
                          <i className="fas fa-plus" />
                        </button>
                      </div>
                    </div>
                  )
                }
              },
              {
                width: 60,
                style: { whiteSpace: "unset" },
                Cell: (cell) => {
                  return (
                    <Button
                      style={{ width: "50px" }}
                      color={"danger"}
                      onClick={() => removeItem(cell.row)}
                    >
                      <div>刪除</div>
                    </Button>
                  )
                }
              }
            ]}
            showPagination={true}
          />
        </CardBody>
        <CardFooter className="d-flex justify-content-between">
          <div className="mt-1" style={{ fontSize: "20px" }}>
            總金額：${subTotal}
          </div>
          <div class="mr-auto p-2 bd-highlight"></div>
          <div style={{ marginRight: "10px" }}>
            <label style={{ fontWeight: "bold" }}>貨品出貨方式</label>
            <div className="form-check">
              <label>
                <input
                  type="radio"
                  value="WITH_RECEIPT"
                  checked={delivery === "WITH_RECEIPT"}
                  onChange={(e) => setDelivery(e.target.value)}
                  className="form-check-input"
                />
                請隨貨開立發票
              </label>
            </div>

            <div className="form-check">
              <label>
                <input
                  type="radio"
                  value="WITHOUT_RECEIPT"
                  checked={delivery === "WITHOUT_RECEIPT"}
                  onChange={(e) => setDelivery(e.target.value)}
                  className="form-check-input"
                />
                貨品先出，發票等通知開立
              </label>
            </div>
          </div>
          <div>
            <Button
              style={{ width: "100px", height: "40px" }}
              color={"primary"}
              disabled={selectedItems.length === 0 || submitting}
              onClick={async () => {
                showAlertModal(async () => {
                  setSubmitting(true)
                  try {
                    const createResult = await createOrderWithDetail({
                      variables: {
                        input: {
                          status: "SUBMITTED",
                          deliveryType: delivery,
                          user: userInfo().id
                        },
                        detail: JSON.stringify(selectedItems)
                      }
                    })
                    setSelectedItems([])
                    setSubmitting(false)
                    setResult(createResult)
                  } catch (err) {
                    setSubmitting(false)
                  }
                })
              }}
            >
              <div>送出</div>
            </Button>
            {result && (
              <Button
                className="ml-2"
                style={{ height: "40px" }}
                color={"warning"}
                onClick={async () => {
                  const { role } = userInfo()
                  console.log(result)
                  if (role === userRole.CUSTOMER) {
                    props.history.push(
                      `/order/detail/${result.data.createOrderWithDetail.id}`
                    )
                  } else {
                    props.history.push(
                      `/order/maintain-order/detail/${result.data.createOrderWithDetail.id}`
                    )
                  }
                }}
              >
                <div>
                  查看訂單 {result.data.createOrderWithDetail.orderNumber}
                </div>
              </Button>
            )}
          </div>
          <BarLoader loading={submitting} />
        </CardFooter>
      </Card>
    </div>
  )
}
const columStyle = { whiteSpace: "unset", textAlign: "center" }

export default withBreadcrumb(withRouter(withModal(ContractProducts)))
